import YouTube, { YouTubeEvent, YouTubeProps } from 'react-youtube';
import { useEffect, useState } from 'react';
import './index.css';

interface VideoItem {
  title?: string;
  url?: string;
}

interface VideoPlayerProps {
  currentVideo: VideoItem | null;
  playerRef:
    | React.MutableRefObject<YouTubeEvent['target'] | null>
    | ((player: YouTubeEvent['target']) => void);
  handlePlay: (event: YouTubeEvent) => void;
  handlePause: (event: YouTubeEvent) => void;
  handleSeek: (event: YouTubeEvent) => void;
  handleError?: (event: YouTubeEvent) => void;
  handleEnd?: () => void;
  className?: string;
}

const VideoPlayer = ({
  currentVideo,
  playerRef,
  handlePlay,
  handlePause,
  handleSeek,
  handleEnd,
  handleError,
  className = '',
}: VideoPlayerProps) => {
  const [videoId, setVideoId] = useState<string | null>(null);
  const [dimensions, setDimensions] = useState({ width: 640, height: 390 });
  const onReady = (event: YouTubeEvent) => {
    const player = event.target;
    if (typeof playerRef === 'function') {
      playerRef(player);
    } else if (playerRef && 'current' in playerRef) {
      playerRef.current = player;
    }
  };

  useEffect(() => {
    if (!currentVideo?.url) {
      setVideoId(null);
      return;
    }

    const { url } = currentVideo;
    let id = '';
    if (url.includes('youtu.be/')) {
      [, id] = url.split('youtu.be/');
    } else if (url.includes('v=')) {
      [, id] = url.split('v=');
    } else if (url.includes('embed/')) {
      [, id] = url.split('embed/');
    }

    const cleanId = id.split(/[?&]/)[0];
    if (cleanId !== videoId) {
      setVideoId(cleanId || null);
    }
  }, [currentVideo, videoId]);

  // Handle responsive sizing
  useEffect(() => {
    const handleResize = () => {
      const width = Math.min(window.innerWidth - 40, 640);
      const height = (width * 9) / 16;
      setDimensions({ width, height });
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const opts: YouTubeProps['opts'] = {
    height: dimensions.height.toString(),
    width: dimensions.width.toString(),
    playerVars: {
      autoplay: 1,
      controls: 1,
      modestbranding: 1,
      rel: 0,
      showinfo: 0,
      fs: 1, // fullscreen button
      iv_load_policy: 3, // hide annotations
      playbackRate: 1,
    },
  };

  const decodeHtml = (html: string) => {
    const txt = document.createElement('textarea');
    txt.innerHTML = html;
    return txt.value;
  };

  return (
    <div className={`video-player-container ${className}`}>
      <h2 className='video-title'>
        {currentVideo?.title ? decodeHtml(currentVideo.title) : 'Loading...'}
      </h2>
      <div className='video-wrapper'>
        {videoId ? (
          <YouTube
            videoId={videoId}
            opts={opts}
            onReady={onReady}
            onPlay={handlePlay}
            onPause={handlePause}
            onSeek={handleSeek}
            onEnd={handleEnd}
            onError={handleError}
            iframeClassName='youtube-iframe'
          />
        ) : (
          <div className='video-placeholder'>
            <p>Select a video from the queue to play</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoPlayer;
